import {
  byteEntropy,
  detectFileFormat,
  printableRatio,
  type DetectedFileFormat,
} from "./file-formats";

const SAMPLE_BYTES = 128 * 1024;
const MAX_OUTPUT_BYTES = 64 * 1024 * 1024;
const MAX_DISCOVERED_CANDIDATES = 12;
const MAX_KEY_TRIALS = 384;

export interface RecoveryKeyInput {
  bytes: Uint8Array;
  label: string;
  source:
    | "manual"
    | "archive string"
    | "known-header inference"
    | "rolling-header inference"
    | "bounded brute force";
}

export type RecoveryStep =
  | { kind: "xor-byte"; key: number }
  | { kind: "xor-rolling"; seed: number; increment: number }
  | { kind: "subtract-byte"; key: number }
  | { kind: "add-byte"; key: number }
  | { kind: "xor-repeating"; key: number[] }
  | { kind: "rc4"; key: number[] }
  | { kind: "bitwise-not" }
  | { kind: "rotate-right"; bits: number }
  | { kind: "rotate-left"; bits: number }
  | { kind: "nibble-swap" }
  | { kind: "reverse" }
  | { kind: "strip-prefix"; offset: number }
  | { kind: "base64" }
  | { kind: "hex" }
  | { kind: "percent-decode" }
  | { kind: "utf16le-text" }
  | { kind: "gzip" }
  | { kind: "deflate" };

export interface RecoveryRecipe {
  id: string;
  label: string;
  steps: RecoveryStep[];
  key?: string;
  keySource?: RecoveryKeyInput["source"];
}

export interface RecoveryCandidate {
  id: string;
  recipe: RecoveryRecipe;
  score: number;
  confidence: "high" | "medium" | "low";
  format: DetectedFileFormat;
  outputSize: number;
  printablePercent: number;
  entropy: number;
  preview: Uint8Array;
  evidence: string[];
}

export interface RecoveryResult {
  inputFormat: DetectedFileFormat;
  inputEntropy: number;
  attemptedTransforms: number;
  attemptedKeys: number;
  truncated: boolean;
  candidates: RecoveryCandidate[];
  limitations: string[];
}

const KNOWN_HEADERS: Array<{ label: string; bytes: number[] }> = [
  { label: "SQLite", bytes: [...new TextEncoder().encode("SQLite format 3\0")] },
  { label: "PNG", bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { label: "ZIP", bytes: [0x50, 0x4b, 0x03, 0x04] },
  { label: "JVM class", bytes: [0xca, 0xfe, 0xba, 0xbe] },
  { label: "Windows PE", bytes: [0x4d, 0x5a, 0x90, 0x00] },
  { label: "ELF", bytes: [0x7f, 0x45, 0x4c, 0x46] },
  { label: "GZIP", bytes: [0x1f, 0x8b, 0x08, 0x00] },
  { label: "PDF", bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] },
  { label: "DEX", bytes: [0x64, 0x65, 0x78, 0x0a, 0x30, 0x33, 0x35, 0x00] },
  { label: "WASM", bytes: [0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00] },
  { label: "Java serialization", bytes: [0xac, 0xed, 0x00, 0x05] },
  { label: "7-Zip", bytes: [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c] },
  { label: "RAR", bytes: [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07, 0x01, 0x00] },
];

const PREFIX_SIGNATURES = KNOWN_HEADERS.map((item) => item.bytes).filter(
  (signature) => signature.length >= 4,
);

function owned(bytes: Uint8Array) {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy;
}

function decodeBase64(bytes: Uint8Array) {
  const raw = new TextDecoder("utf-8", { fatal: false })
    .decode(bytes)
    .trim()
    .replace(/^data:[^,]*;base64,/i, "")
    .replace(/\s+/g, "");
  if (
    raw.length < 8 ||
    raw.length % 4 === 1 ||
    !/^[A-Za-z0-9+/_-]+={0,2}$/.test(raw)
  ) {
    throw new Error("Input is not a complete Base64 value.");
  }
  const normalized = raw.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = globalThis.atob(padded);
  if (binary.length > MAX_OUTPUT_BYTES) {
    throw new Error("Decoded output exceeds the 64 MB recovery limit.");
  }
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function decodeHex(bytes: Uint8Array) {
  const raw = new TextDecoder("utf-8", { fatal: false })
    .decode(bytes)
    .trim()
    .replace(/^0x/i, "")
    .replace(/(?:\\x|0x|[\s,;:_-])/gi, "");
  if (raw.length < 8 || raw.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(raw)) {
    throw new Error("Input is not a complete hexadecimal byte string.");
  }
  if (raw.length / 2 > MAX_OUTPUT_BYTES) {
    throw new Error("Decoded output exceeds the 64 MB recovery limit.");
  }
  const output = new Uint8Array(raw.length / 2);
  for (let index = 0; index < output.length; index += 1) {
    output[index] = Number.parseInt(raw.slice(index * 2, index * 2 + 2), 16);
  }
  return output;
}

function decodePercentEncoded(bytes: Uint8Array) {
  const raw = new TextDecoder("utf-8", { fatal: false }).decode(bytes).trim();
  if ((raw.match(/%[0-9a-f]{2}/gi) ?? []).length < 3) {
    throw new Error("Input does not contain enough percent-encoded bytes.");
  }
  const decoded = globalThis.decodeURIComponent(raw.replaceAll("+", "%20"));
  return new TextEncoder().encode(decoded);
}

function decodeUtf16LeText(bytes: Uint8Array) {
  if (bytes.length < 8 || bytes.length % 2 !== 0) {
    throw new Error("Input is not a complete UTF-16LE stream.");
  }
  const text = new TextDecoder("utf-16le", { fatal: false })
    .decode(bytes)
    .replace(/^\uFEFF/, "");
  return new TextEncoder().encode(text);
}

function xorRepeating(bytes: Uint8Array, key: Uint8Array) {
  if (!key.length) throw new Error("An XOR key cannot be empty.");
  const output = new Uint8Array(bytes.length);
  for (let index = 0; index < bytes.length; index += 1) {
    output[index] = bytes[index] ^ key[index % key.length];
  }
  return output;
}

function rc4(bytes: Uint8Array, key: Uint8Array) {
  if (!key.length) throw new Error("An RC4 key cannot be empty.");
  const state = Uint8Array.from({ length: 256 }, (_, index) => index);
  let cursor = 0;
  for (let index = 0; index < 256; index += 1) {
    cursor = (cursor + state[index] + key[index % key.length]) & 0xff;
    [state[index], state[cursor]] = [state[cursor], state[index]];
  }
  const output = new Uint8Array(bytes.length);
  let left = 0;
  let right = 0;
  for (let index = 0; index < bytes.length; index += 1) {
    left = (left + 1) & 0xff;
    right = (right + state[left]) & 0xff;
    [state[left], state[right]] = [state[right], state[left]];
    output[index] = bytes[index] ^ state[(state[left] + state[right]) & 0xff];
  }
  return output;
}

async function decompress(
  bytes: Uint8Array,
  format: "gzip" | "deflate",
  maxOutputBytes: number,
) {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser does not expose the decompression stream API.");
  }
  const stream = new Blob([owned(bytes).buffer])
    .stream()
    .pipeThrough(new DecompressionStream(format));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = value instanceof Uint8Array ? value : new Uint8Array(value);
      total += chunk.length;
      if (total > maxOutputBytes) {
        await reader.cancel();
        throw new Error(
          `Decompressed output exceeds the ${Math.round(maxOutputBytes / 1024 / 1024)} MB limit.`,
        );
      }
      chunks.push(chunk);
    }
  } finally {
    reader.releaseLock();
  }
  const output = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

export async function applyRecoveryRecipe(
  input: Uint8Array,
  recipe: RecoveryRecipe,
  maxOutputBytes = MAX_OUTPUT_BYTES,
) {
  let bytes = owned(input);
  for (const step of recipe.steps) {
    if (bytes.length > maxOutputBytes) {
      throw new Error("Recovery input exceeds the configured output limit.");
    }
    if (step.kind === "xor-byte") {
      const output = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        output[index] = bytes[index] ^ step.key;
      }
      bytes = output;
    } else if (step.kind === "xor-rolling") {
      const output = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        const key = (step.seed + step.increment * index) & 0xff;
        output[index] = bytes[index] ^ key;
      }
      bytes = output;
    } else if (step.kind === "subtract-byte") {
      const output = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        output[index] = (bytes[index] - step.key) & 0xff;
      }
      bytes = output;
    } else if (step.kind === "add-byte") {
      const output = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        output[index] = (bytes[index] + step.key) & 0xff;
      }
      bytes = output;
    } else if (step.kind === "xor-repeating") {
      bytes = xorRepeating(bytes, Uint8Array.from(step.key));
    } else if (step.kind === "rc4") {
      bytes = rc4(bytes, Uint8Array.from(step.key));
    } else if (step.kind === "bitwise-not") {
      bytes = bytes.map((byte) => byte ^ 0xff);
    } else if (step.kind === "rotate-right") {
      const bits = step.bits & 7;
      bytes = bytes.map(
        (byte) => ((byte >>> bits) | (byte << (8 - bits))) & 0xff,
      );
    } else if (step.kind === "rotate-left") {
      const bits = step.bits & 7;
      bytes = bytes.map(
        (byte) => ((byte << bits) | (byte >>> (8 - bits))) & 0xff,
      );
    } else if (step.kind === "nibble-swap") {
      bytes = bytes.map((byte) => ((byte >>> 4) | (byte << 4)) & 0xff);
    } else if (step.kind === "reverse") {
      bytes = owned(bytes).reverse();
    } else if (step.kind === "strip-prefix") {
      if (step.offset <= 0 || step.offset >= bytes.length) {
        throw new Error("The recovered prefix offset is outside the file.");
      }
      bytes = owned(bytes.subarray(step.offset));
    } else if (step.kind === "base64") {
      bytes = decodeBase64(bytes);
    } else if (step.kind === "hex") {
      bytes = decodeHex(bytes);
    } else if (step.kind === "percent-decode") {
      bytes = decodePercentEncoded(bytes);
    } else if (step.kind === "utf16le-text") {
      bytes = decodeUtf16LeText(bytes);
    } else if (step.kind === "gzip" || step.kind === "deflate") {
      bytes = await decompress(bytes, step.kind, maxOutputBytes);
    }
  }
  if (bytes.length > maxOutputBytes) {
    throw new Error("Recovered output exceeds the 64 MB limit.");
  }
  return bytes;
}

function keyHex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function keyText(bytes: Uint8Array) {
  const printable = Array.from(bytes).every(
    (byte) => byte >= 0x20 && byte <= 0x7e,
  );
  return printable
    ? new TextDecoder().decode(bytes)
    : `hex:${keyHex(bytes)}`;
}

function candidateScore(
  inputSample: Uint8Array,
  output: Uint8Array,
  format: DetectedFileFormat,
) {
  const inputPrintable = printableRatio(inputSample);
  const outputPrintable = printableRatio(output);
  const inputEntropy = byteEntropy(inputSample);
  const outputEntropy = byteEntropy(output);
  let score = 0;
  const evidence: string[] = [];

  if (format.id === "json" && format.confidence === "content") {
    score += 64;
    evidence.push("Recovered valid JSON structure");
  } else if (format.confidence === "signature" && format.id !== "binary") {
    score += 76;
    evidence.push(`Recovered ${format.label} magic and structure`);
    if (format.id === "class" || format.id === "zip" || format.id === "jar" || format.id === "gzip") {
      score += 12;
      evidence.push(`High-value archive/class container signature (${format.label})`);
    }
  } else if (format.confidence === "content" && format.preview === "text") {
    score += 42;
    evidence.push("Recovered a coherent printable text stream");
  } else if (format.id !== "binary" && format.confidence !== "unknown") {
    score += 24;
  }
  if (outputPrintable >= 0.9) {
    score += 18;
    evidence.push(`${Math.round(outputPrintable * 100)}% printable output`);
  } else if (outputPrintable >= 0.72) {
    score += 10;
    evidence.push(`${Math.round(outputPrintable * 100)}% printable output`);
  }
  const printableGain = outputPrintable - inputPrintable;
  if (printableGain >= 0.35) {
    score += 10;
    evidence.push(
      `Printable ratio improved by ${Math.round(printableGain * 100)} points`,
    );
  }
  if (
    inputEntropy >= 7.2 &&
    outputEntropy <= inputEntropy - 0.8 &&
    outputPrintable >= 0.65
  ) {
    score += 6;
    evidence.push(`Entropy fell from ${inputEntropy} to ${outputEntropy}`);
  }
  if (
    output.length >= 8 &&
    output.every((byte) => byte === 0 || byte === 0xff)
  ) {
    score -= 50;
  }
  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    evidence,
  };
}

function recipeId(steps: RecoveryStep[]) {
  return steps
    .map((step) => {
      if (step.kind === "xor-byte" || step.kind === "subtract-byte")
        return `${step.kind}-${step.key}`;
      if (step.kind === "xor-rolling")
        return `${step.kind}-${step.seed}-${step.increment}`;
      if (step.kind === "xor-repeating" || step.kind === "rc4")
        return `${step.kind}-${keyHex(Uint8Array.from(step.key))}`;
      if (step.kind === "rotate-right") return `${step.kind}-${step.bits}`;
      if (step.kind === "strip-prefix") return `${step.kind}-${step.offset}`;
      return step.kind;
    })
    .join("+");
}

function findHeaderOffsets(bytes: Uint8Array) {
  const offsets = new Set<number>();
  const limit = Math.min(bytes.length, 4_096);
  for (const signature of PREFIX_SIGNATURES) {
    for (let offset = 1; offset + signature.length <= limit; offset += 1) {
      let matches = true;
      for (let index = 0; index < signature.length; index += 1) {
        if (bytes[offset + index] !== signature[index]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        offsets.add(offset);
        break;
      }
    }
  }
  return [...offsets].slice(0, 8);
}

function inferredHeaderKeys(bytes: Uint8Array) {
  const keys: RecoveryKeyInput[] = [];
  const seen = new Set<string>();
  for (const header of KNOWN_HEADERS) {
    const maximum = Math.min(8, Math.floor(header.bytes.length / 2));
    for (let length = 2; length <= maximum; length += 1) {
      const key = new Uint8Array(length);
      const set = new Uint8Array(length);
      let valid = true;
      for (let index = 0; index < header.bytes.length; index += 1) {
        const position = index % length;
        const value = bytes[index] ^ header.bytes[index];
        if (set[position] && key[position] !== value) {
          valid = false;
          break;
        }
        key[position] = value;
        set[position] = 1;
      }
      if (!valid || set.some((value) => value === 0)) continue;
      const id = keyHex(key);
      if (seen.has(id)) continue;
      seen.add(id);
      keys.push({
        bytes: key,
        label: `${keyText(key)} → ${header.label}`,
        source: "known-header inference",
      });
    }
  }
  return keys;
}

function inferredRollingRecipes(bytes: Uint8Array) {
  const recipes: Array<Omit<RecoveryRecipe, "id">> = [];
  const seen = new Set<string>();
  for (const header of KNOWN_HEADERS) {
    if (header.bytes.length < 4 || bytes.length < header.bytes.length) continue;
    for (let increment = -16; increment <= 16; increment += 1) {
      if (increment === 0) continue;
      const seed = bytes[0] ^ header.bytes[0];
      let valid = true;
      for (let index = 1; index < header.bytes.length; index += 1) {
        const key = (seed + increment * index) & 0xff;
        if ((bytes[index] ^ key) !== header.bytes[index]) {
          valid = false;
          break;
        }
      }
      if (!valid) continue;
      const id = `${seed}:${increment}`;
      if (seen.has(id)) continue;
      seen.add(id);
      recipes.push({
        label: `Rolling XOR seed 0x${seed.toString(16).padStart(2, "0")}, increment ${increment > 0 ? "+" : ""}${increment} → ${header.label}`,
        steps: [{ kind: "xor-rolling", seed, increment }],
        key: `seed 0x${seed.toString(16).padStart(2, "0")} · ${increment > 0 ? "+" : ""}${increment}/byte`,
        keySource: "rolling-header inference",
      });
    }
  }
  return recipes.slice(0, 32);
}

function dedupeKeys(keys: RecoveryKeyInput[]) {
  const seen = new Set<string>();
  const result: RecoveryKeyInput[] = [];
  for (const item of keys) {
    if (
      !item.bytes.length ||
      item.bytes.length > 64 ||
      item.bytes.every((byte) => byte === 0)
    ) {
      continue;
    }
    const id = keyHex(item.bytes);
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(item);
    if (result.length >= MAX_KEY_TRIALS) break;
  }
  return result;
}

export function parseManualKeyList(value: string): RecoveryKeyInput[] {
  const result: RecoveryKeyInput[] = [];
  for (const rawLine of value.split(/\r?\n/).slice(0, 256)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    let bytes: Uint8Array | null = null;
    const label = line;
    try {
      if (/^hex:/i.test(line)) {
        const hex = line.slice(4).replace(/[\s:_-]/g, "");
        if (hex.length >= 2 && hex.length % 2 === 0 && /^[0-9a-f]+$/i.test(hex)) {
          bytes = new Uint8Array(hex.length / 2);
          for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
          }
        }
      } else if (/^(?:b64|base64):/i.test(line)) {
        const encoded = line.slice(line.indexOf(":") + 1).replace(/\s+/g, "");
        const binary = globalThis.atob(encoded);
        bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      } else {
        bytes = new TextEncoder().encode(line);
      }
    } catch {
      bytes = null;
    }
    if (bytes?.length && bytes.length <= 64) {
      result.push({ bytes, label, source: "manual" });
    }
  }
  return dedupeKeys(result);
}

export function archiveStringKeys(values: string[]): RecoveryKeyInput[] {
  const result: RecoveryKeyInput[] = [];
  const seen = new Set<string>();
  for (const raw of values.slice(0, 4_000)) {
    const value = raw.trim();
    if (
      value.length < 2 ||
      value.length > 64 ||
      /[\r\n\0]/.test(value) ||
      !/^[\x20-\x7e]+$/.test(value)
    ) {
      continue;
    }
    const looksLikeKey =
      /(?:key|secret|password|passwd|pwd|xor|decrypt|cipher|aes|rc4)/i.test(
        value,
      ) ||
      /^[A-Za-z0-9_!@#$%^&*+=.-]{4,32}$/.test(value);
    if (!looksLikeKey) continue;
    const bytes = new TextEncoder().encode(value);
    const id = keyHex(bytes);
    if (seen.has(id)) continue;
    seen.add(id);
    result.push({ bytes, label: value, source: "archive string" });
    if (result.length >= 192) break;
  }
  return result;
}


function prefersRecipe(left: RecoveryRecipe, right: RecoveryRecipe) {
  if (left.steps.length !== right.steps.length) {
    return left.steps.length < right.steps.length;
  }
  const keyMagnitude = (recipe: RecoveryRecipe) => {
    const step = recipe.steps[0];
    if (!step) return Number.POSITIVE_INFINITY;
    if (step.kind === "xor-byte" || step.kind === "subtract-byte" || step.kind === "add-byte") {
      return step.key;
    }
    if (step.kind === "rotate-right" || step.kind === "rotate-left") {
      return step.bits;
    }
    return Number.POSITIVE_INFINITY;
  };
  const leftKey = keyMagnitude(left);
  const rightKey = keyMagnitude(right);
  if (leftKey !== rightKey) return leftKey < rightKey;
  // Prefer additive form over the modular subtract complement when both recover the same bytes.
  const leftKind = left.steps[0]?.kind ?? "";
  const rightKind = right.steps[0]?.kind ?? "";
  if (leftKind === "add-byte" && rightKind === "subtract-byte") return true;
  if (leftKind === "subtract-byte" && rightKind === "add-byte") return false;
  return left.label.localeCompare(right.label) < 0;
}

export async function discoverRecoveryCandidates(
  input: Uint8Array,
  path: string,
  candidateKeys: RecoveryKeyInput[] = [],
): Promise<RecoveryResult> {
  const inputFormat = detectFileFormat(input, path);
  const sample = owned(input.subarray(0, Math.min(input.length, SAMPLE_BYTES)));
  const inputEntropy = byteEntropy(sample);
  const discovered = new Map<string, RecoveryCandidate>();
  let attemptedTransforms = 0;
  let attemptedKeys = 0;

  const consider = async (
    recipe: Omit<RecoveryRecipe, "id">,
    minimumScore = 58,
  ) => {
    attemptedTransforms += 1;
    if (attemptedTransforms % 48 === 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
    const fullRecipe: RecoveryRecipe = {
      ...recipe,
      id: recipeId(recipe.steps),
    };
    try {
      const needsWholeInput = recipe.steps.some(
        (step) =>
          step.kind === "gzip" ||
          step.kind === "deflate" ||
          ((step.kind === "base64" || step.kind === "hex") &&
            input.length <= 16 * 1024 * 1024),
      );
      const evaluationInput = needsWholeInput ? input : sample;
      const output = await applyRecoveryRecipe(
        evaluationInput,
        fullRecipe,
        Math.max(MAX_OUTPUT_BYTES, SAMPLE_BYTES * 8),
      );
      if (!output.length) return;
      if (
        output.length === evaluationInput.length &&
        output.every((byte, index) => byte === evaluationInput[index])
      ) {
        return;
      }
      const preview = owned(output.subarray(0, Math.min(output.length, SAMPLE_BYTES)));
      const format = detectFileFormat(preview, "recovered.bin");
      const scored = candidateScore(sample, preview, format);
      if (scored.score < minimumScore) return;
      const signature = `${format.id}:${Array.from(preview.subarray(0, 48)).join(",")}`;
      const current = discovered.get(signature);
      const candidate: RecoveryCandidate = {
        id: fullRecipe.id,
        recipe: fullRecipe,
        score: scored.score,
        confidence:
          scored.score >= 90 ? "high" : scored.score >= 70 ? "medium" : "low",
        format,
        outputSize: needsWholeInput
          ? output.length
          : recipe.steps[0]?.kind === "strip-prefix"
            ? Math.max(0, input.length - recipe.steps[0].offset)
            : input.length,
        printablePercent: Math.round(printableRatio(preview) * 100),
        entropy: byteEntropy(preview),
        preview,
        evidence: [
          ...scored.evidence,
          ...(recipe.keySource
            ? [`Key source: ${recipe.keySource}`]
            : []),
        ],
      };
      if (!current || candidate.score > current.score || (candidate.score === current.score && prefersRecipe(candidate.recipe, current.recipe))) {
        discovered.set(signature, candidate);
      }
    } catch {
      // Invalid speculative transforms are expected during bounded recovery.
    }
  };

  const inputText = new TextDecoder("utf-8", { fatal: false }).decode(sample);
  if (
    /^[\sA-Za-z0-9+/_=-]{8,}$/.test(inputText.trim()) &&
    inputText.trim().length % 4 !== 1
  ) {
    await consider({ label: "Decode Base64", steps: [{ kind: "base64" }] }, 45);
  }
  if (
    /^(?:0x)?(?:[\s,;:_-]*(?:\\x|0x)?[0-9a-fA-F]{2}){4,}[\s,;:_-]*$/.test(
      inputText.trim(),
    )
  ) {
    await consider({ label: "Decode hexadecimal bytes", steps: [{ kind: "hex" }] }, 45);
  }
  if ((inputText.match(/%[0-9a-f]{2}/gi) ?? []).length >= 3) {
    await consider(
      {
        label: "Decode percent-encoded bytes",
        steps: [{ kind: "percent-decode" }],
      },
      42,
    );
  }
  const oddNulls = sample.filter((byte, index) => index % 2 === 1 && byte === 0)
    .length;
  if (
    sample.length >= 8 &&
    sample.length % 2 === 0 &&
    oddNulls / Math.max(1, sample.length / 2) >= 0.45
  ) {
    await consider(
      {
        label: "Decode UTF-16LE text",
        steps: [{ kind: "utf16le-text" }],
      },
      40,
    );
  }
  if (inputFormat.id === "gzip") {
    await consider(
      { label: "Decompress GZIP stream", steps: [{ kind: "gzip" }] },
      20,
    );
  }

  for (const offset of findHeaderOffsets(sample)) {
    await consider(
      {
        label: `Strip ${offset.toLocaleString()} prepended bytes`,
        steps: [{ kind: "strip-prefix", offset }],
      },
      60,
    );
  }

  await consider({ label: "Invert every bit", steps: [{ kind: "bitwise-not" }] });
  await consider({ label: "Swap high and low nibbles", steps: [{ kind: "nibble-swap" }] });
  await consider({ label: "Reverse byte order", steps: [{ kind: "reverse" }] });
  for (let bits = 1; bits <= 7; bits += 1) {
    await consider({
      label: `Rotate each byte right by ${bits}`,
      steps: [{ kind: "rotate-right", bits }],
    });
  }
  for (let bits = 1; bits <= 7; bits += 1) {
    await consider({
      label: `Rotate each byte left by ${bits}`,
      steps: [{ kind: "rotate-left", bits }],
    });
  }
  for (const recipe of inferredRollingRecipes(sample)) {
    await consider(recipe, 66);
  }

  for (let key = 1; key <= 255; key += 1) {
    await consider(
      {
        label: `Single-byte XOR with 0x${key.toString(16).padStart(2, "0")}`,
        steps: [{ kind: "xor-byte", key }],
        key: `0x${key.toString(16).padStart(2, "0")}`,
        keySource: "bounded brute force",
      },
      66,
    );
  }
  for (let key = 1; key <= 255; key += 1) {
    await consider(
      {
        label: `Subtract byte key 0x${key.toString(16).padStart(2, "0")}`,
        steps: [{ kind: "subtract-byte", key }],
        key: `0x${key.toString(16).padStart(2, "0")}`,
        keySource: "bounded brute force",
      },
      68,
    );
  }

  for (let key = 1; key <= 255; key += 1) {
    await consider(
      {
        label: `Add byte key 0x${key.toString(16).padStart(2, "0")}`,
        steps: [{ kind: "add-byte", key }],
        key: `0x${key.toString(16).padStart(2, "0")}`,
        keySource: "bounded brute force",
      },
      68,
    );
  }

  const keys = dedupeKeys([...inferredHeaderKeys(sample), ...candidateKeys]);
  for (const item of keys) {
    attemptedKeys += 1;
    const display = keyText(item.bytes);
    await consider(
      {
        label: `Repeating-key XOR with ${display}`,
        steps: [{ kind: "xor-repeating", key: [...item.bytes] }],
        key: display,
        keySource: item.source,
      },
      item.source === "manual" ? 18 : 62,
    );
    if (item.bytes.length >= 3) {
      await consider(
        {
          label: `RC4 candidate key ${display}`,
          steps: [{ kind: "rc4", key: [...item.bytes] }],
          key: display,
          keySource: item.source,
        },
        item.source === "manual" ? 18 : 68,
      );
    }
  }

  const firstLayer = [...discovered.values()]
    .sort((left, right) => right.score - left.score)
    .slice(0, 12);
  for (const candidate of firstLayer) {
    let output: Uint8Array;
    try {
      output = await applyRecoveryRecipe(
        sample,
        candidate.recipe,
        SAMPLE_BYTES * 8,
      );
    } catch {
      continue;
    }
    const chain = (
      label: string,
      step: RecoveryStep,
      minimumScore = 62,
    ) =>
      consider(
        {
          label: `${candidate.recipe.label} → ${label}`,
          steps: [...candidate.recipe.steps, step],
          key: candidate.recipe.key,
          keySource: candidate.recipe.keySource,
        },
        minimumScore,
      );
    const outputFormat = detectFileFormat(output, "nested-recovery.bin");
    if (outputFormat.id === "gzip") {
      await chain("decompress GZIP", { kind: "gzip" }, 42);
    }
    // Many loaders XOR then raw-deflate; try deflate even without gzip magic.
    if (output.length >= 32) {
      await chain("decompress raw DEFLATE", { kind: "deflate" }, 55);
    }
    const outputText = new TextDecoder("utf-8", { fatal: false })
      .decode(output)
      .trim();
    if (
      outputText.length >= 8 &&
      outputText.length % 4 !== 1 &&
      /^[A-Za-z0-9+/_=-]+$/.test(outputText)
    ) {
      await chain("decode Base64", { kind: "base64" }, 48);
    }
    if (
      /^(?:0x)?(?:[\s,;:_-]*(?:\\x|0x)?[0-9a-fA-F]{2}){4,}[\s,;:_-]*$/.test(
        outputText,
      )
    ) {
      await chain("decode hexadecimal bytes", { kind: "hex" }, 48);
    }
    for (const rolling of inferredRollingRecipes(output).slice(0, 6)) {
      await consider(
        {
          label: `${candidate.recipe.label} → ${rolling.label}`,
          steps: [...candidate.recipe.steps, ...rolling.steps],
          key: rolling.key,
          keySource: rolling.keySource,
        },
        66,
      );
    }
  }

  return {
    inputFormat,
    inputEntropy,
    attemptedTransforms,
    attemptedKeys,
    truncated: input.length > sample.length,
    candidates: [...discovered.values()]
      .sort(
        (left, right) =>
          right.score - left.score ||
          Number(right.format.confidence === "signature") -
            Number(left.format.confidence === "signature") ||
          left.recipe.label.localeCompare(right.recipe.label),
      )
      .slice(0, MAX_DISCOVERED_CANDIDATES),
    limitations: [
      `Automatic recovery evaluates at most ${Math.round(SAMPLE_BYTES / 1024)} KB per transform.`,
      `Full recovered downloads are capped at ${Math.round(MAX_OUTPUT_BYTES / 1024 / 1024)} MB.`,
      "Nested recovery is bounded to two evidence-backed transform layers.",
      "AES, ChaCha20, and other modern encryption cannot be meaningfully brute-forced here. Supply a real candidate key or analyze the key derivation in code.",
      "A high-scoring transform is evidence of structure, not proof that every recovered byte is correct.",
    ],
  };
}
